/**
 * ← Week 3 → — the control at the top of Timeline, Shouts and the picks board.
 *
 * Two rules, both about weeks an admin has switched off:
 *
 *   - the arrows step over them, because stepping *onto* one would show an
 *     empty screen and leave the reader tapping again to find out why;
 *   - the current week still renders its own label even when it is one of them,
 *     because arriving by link at a disabled week and being told a different
 *     week's name is worse than being told the truth.
 *
 * `←` is backwards in time and sits on the left, which is the direction a list
 * ordered newest-first is already running.
 */

import type { WeekOption } from '@shared/types'
import { weekRange } from '../lib/dates'

export function WeekSwitcher({
  weeks, weekId, onChange, label,
}: {
  weeks: WeekOption[]
  weekId: string | null
  onChange: (id: string) => void
  /** Shown when `weeks` hasn't loaded yet, so the bar doesn't pop in. */
  label?: string
}) {
  const enabled = weeks.filter((w) => w.isEnabled)
  const index = enabled.findIndex((w) => w.id === weekId)
  const current = weeks.find((w) => w.id === weekId)

  // A disabled current week is not in `enabled`, so `index` is -1. Fall back to
  // the nearest enabled week on either side by start time.
  const prev = index >= 0
    ? enabled[index - 1]
    : [...enabled].reverse().find((w) => current && w.startsAt < current.startsAt)
  const next = index >= 0
    ? enabled[index + 1]
    : enabled.find((w) => current && w.startsAt > current.startsAt)

  const arrow = (dir: 'prev' | 'next', target: WeekOption | undefined) => (
    <button
      onClick={() => { if (target) onChange(target.id) }}
      disabled={!target}
      aria-label={dir === 'prev' ? 'Previous week' : 'Next week'}
      style={{
        width: 44, height: 44, flex: '0 0 auto', borderRadius: 12,
        border: '1px solid var(--outline)',
        color: target ? 'var(--fg)' : 'var(--line)',
        fontSize: 16, fontWeight: 700,
        opacity: target ? 1 : 0.45,
      }}
    >
      {dir === 'prev' ? '←' : '→'}
    </button>
  )

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {arrow('prev', prev)}
      <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
        <div className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
          {current?.label ?? label ?? '—'}
        </div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--dim)', marginTop: 2 }}>
          {current && !current.isEnabled
            ? 'switched off'
            : current
              ? `${weekRange(current.startsAt, current.endsAt)}${current.isCurrent ? ' · this week' : ''}`
              : ''}
        </div>
      </div>
      {arrow('next', next)}
    </div>
  )
}
